import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const EMPTY = { memberId: null, data: null, error: null };

/**
 * Loads the data of one member tab. `load` must be stable (`useCallback`) and resolve the data or
 * throw. `isLoading` is derived (the last settled request is not for this member); `refresh`
 * refetches in place: when it fails after the first load the data on screen is kept.
 *
 * @template T
 * @param {string} memberId
 * @param {() => Promise<T>} load
 * @param {{ enabled?: boolean }} [options] `enabled: false` (a removed member) fetches nothing.
 * `refresh` resolves `true` when it worked, `false` when it failed and `null` when it was ignored
 * (it belonged to a previous member). `retry` is for the Try again button: after a failed load it
 * shows the loading state again while it fetches.
 *
 * @returns {{ data: T|null, isLoading: boolean, error: string|null,
 *   refresh: () => Promise<boolean|null>, retry: () => void }}
 */
export const useMemberTabData = (memberId, load, { enabled = true } = {}) => {
  const [settled, setSettled] = useState(EMPTY);
  const fetchIdRef = useRef(0);
  const mountedRef = useRef(true);
  const memberIdRef = useRef(memberId);

  const run = useCallback(() => {
    // A refresh left over from another member must not discard the fetch of the current one.
    if (memberIdRef.current !== memberId) return Promise.resolve(null);
    fetchIdRef.current += 1;
    const fetchId = fetchIdRef.current;
    return Promise.resolve()
      .then(load)
      .then((data) => ({ memberId, data, error: null }))
      .catch((err) => ({ memberId, data: null, error: err?.message ?? 'failed' }))
      .then((next) => {
        if (mountedRef.current && fetchId === fetchIdRef.current) {
          // A refresh that fails keeps the data already on screen.
          setSettled((prev) => (next.error && prev.memberId === memberId && prev.data ? prev : next));
        }
        return next.error === null;
      });
  }, [memberId, load]);

  useEffect(() => {
    memberIdRef.current = memberId;
  }, [memberId]);

  useEffect(() => {
    mountedRef.current = true;
    if (enabled) run();
    return () => {
      mountedRef.current = false;
    };
  }, [run, enabled]);

  const retry = useCallback(() => {
    setSettled(EMPTY);
    run();
  }, [run]);

  const isCurrent = settled.memberId === memberId;

  return useMemo(
    () => ({
      data: enabled && isCurrent ? settled.data : null,
      isLoading: enabled && !isCurrent,
      error: enabled && isCurrent ? settled.error : null,
      refresh: run,
      retry,
    }),
    [enabled, isCurrent, settled.data, settled.error, run, retry],
  );
};
